import type { Trip } from '@/services/trips';
import type { TripAlert } from '@/utils/tripAlerts';
import type { TripBudgetSnapshot } from '@/types/budget';
import { formatCurrency } from '@/utils/currency';
import { DEFAULT_BUDGET_ALERT_THRESHOLDS } from '@/services/trips';

const formatPercent = (value: number): string =>
  `${Math.round(value * 100)}%`;

export function generateBudgetAlertForTrip(
  trip: Trip,
  snapshot?: TripBudgetSnapshot,
): TripAlert[] {
  if (!snapshot) return [];
  if (!trip.budget || trip.budget <= 0) return [];
  if (trip.isArchived) return [];

  const thresholds =
    trip.budgetAlertThresholds && trip.budgetAlertThresholds.length > 0
      ? [...trip.budgetAlertThresholds].sort((a, b) => a - b)
      : DEFAULT_BUDGET_ALERT_THRESHOLDS;

  const totalSpent = snapshot.expensesConverted + snapshot.bookingsConverted;
  const percentUsed = totalSpent / trip.budget;

  const highestThreshold = thresholds.reduce<number | null>(
    (acc, threshold) => (percentUsed >= threshold ? threshold : acc),
    null,
  );

  if (highestThreshold === null) {
    return [];
  }

  const baseCurrency = trip.baseCurrency || 'USD';
  const spentLabel = formatCurrency(totalSpent, baseCurrency);
  const budgetLabel = formatCurrency(trip.budget, baseCurrency);
  const remaining = Math.max(trip.budget - totalSpent, 0);
  const remainingLabel = formatCurrency(remaining, baseCurrency);

  const isOverBudget = percentUsed >= 1;
  const priority: TripAlert['priority'] =
    isOverBudget || highestThreshold >= 0.95
      ? 'high'
      : highestThreshold >= 0.85
      ? 'medium'
      : 'low';

  const percentLabel = formatPercent(percentUsed);
  const thresholdPercent = formatPercent(highestThreshold);

  const message = isOverBudget
    ? `You've spent ${spentLabel}, which exceeds your planned budget of ${budgetLabel}. Consider adjusting your plans or updating the budget.`
    : `You've used ${spentLabel} of your ${budgetLabel} budget (${percentLabel}). You have ${remainingLabel} remaining.`;

  const alert: TripAlert = {
    id: `budget-${trip.id}-${Math.round(highestThreshold * 100)}`,
    type: 'budget',
    title: isOverBudget
      ? `Budget exceeded`
      : `Budget ${thresholdPercent} reached`,
    message,
    tripId: trip.id,
    tripName: trip.name,
    actionLabel: 'Review budget',
    priority,
    budgetMetadata: {
      thresholdPercent: highestThreshold,
      spentAmount: totalSpent,
      budgetAmount: trip.budget,
      spentFormatted: spentLabel,
      budgetFormatted: budgetLabel,
      isOverBudget,
    },
  };

  return [alert];
}

